import { FlightPax, FlightProduct } from '@/api/Cart/cart.interfaces';
import { isEmpty } from 'lodash';
import { getSelectedBaggages } from '@shared/helpers/baggage';
import { Money } from '@libs/money/money.interfaces';
import {
    getAllSeatsForFlightProduct,
    getSeatingTotalPriceForFlightProduct,
} from '@/helpers/seating';
import { t } from '@shared/helpers/translation';
import { LabelFormat } from '@shared/interfaces/translation';

const getSeatingPrice = (product: FlightProduct): number => {
    return product.Seating?.Price.Amount || 0;
};

const getGordianSeatingPrice = (product: FlightProduct): number => {
    return getSeatingTotalPriceForFlightProduct(product)?.Amount ?? 0;
};

const getBaggagePrice = (product: FlightProduct): number => {
    return product.Pax.reduce((sum: number, pax: FlightPax) => {
        if (pax.AdditionalBaggage) {
            const selected = getSelectedBaggages(pax.AdditionalBaggage);

            selected.forEach((x) => {
                sum += x.Price?.Amount;
            });
        }
        return sum;
    }, 0);
};

export const getBaggageSelectedCount = (product: FlightProduct): number => {
    return product.Pax.reduce((count, pax) => {
        const selected = getSelectedBaggages(pax.AdditionalBaggage);

        if (selected && !isEmpty(selected)) {
            selected.forEach((s) => {
                count += s.Pieces || 1;
            });
        }

        return count;
    }, 0);
};

export const mapFlightPrice = (product: FlightProduct): Money => {
    return {
        Amount:
            product.Price.Amount +
            getBaggagePrice(product) +
            getSeatingPrice(product) +
            getGordianSeatingPrice(product),
        Currency: product.Price.Currency,
    };
};

export const mapFlightLabel = (product: FlightProduct): string => {
    const firstLeg = product.Legs[0];
    const firstSegment = firstLeg.Segments[0];
    const lastSegment = firstLeg.Segments[firstLeg.Segments.length - 1];
    return `${firstSegment.DepartureAirportLabel} (${firstSegment.DepartureAirportCode}) - ${lastSegment.ArrivalAirportLabel} (${lastSegment.ArrivalAirportCode})`;
};

export const mapFlightSubLabel = (product: FlightProduct): string => {
    const gordianSeatingLabel = getGordianSeatingLabel(product);

    const roundtrip =
        product.Legs.length > 1 ? t({ prefix: ', ', key: 'shared.roundtrip' }) : '';

    const baggage = getBaggageSelectedCount(product);
    const baggageLabel =
        baggage > 0
            ? `, ${baggage} ${t({ key: 'shared.baggage', format: LabelFormat.LowerCase })}`
            : '';

    const tickets = product.Pax.length;
    const ticketsLabel = `${tickets} ${t({
        key: 'shared.ticket',
        keyPlural: 'shared.tickets',
        count: tickets,
    })}`;

    return `${ticketsLabel}${baggageLabel}${gordianSeatingLabel}${roundtrip}`;
};

const getGordianSeatingLabel = (product: FlightProduct) => {
    const seats = getAllSeatsForFlightProduct(product).length;
    if (seats) {
        return `, ${seats} ${t({
            key: 'seating.seat',
            keyPlural: 'seating.seats',
            count: seats,
        })}`.toLowerCase();
    }
    return '';
};
